import { type KeyboardEvent, memo, type ChangeEvent } from 'react'
import { SORT_BY_FACET, SEARCH_FILTER_TYPE } from '@/features/shared/constants/global'
import { DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import { isButtonActionEvent } from '@/features/shared/utils/keyEvents'
import { RadioGroup } from '@shipt/design-system-radio-group'
import { useOnChangeFilter } from '@/features/shop/components/ProductFilter/interactions/useOnChangeFilter'
import { useTrackFilterUtilityClicked } from '@/features/shared/analytics/filters'
import { useGetSortByOptions } from '@/features/shop/components/ProductFilter/useGetSortByOptions'
import { useCurrentCategory } from '@/features/shop/components/ProductFilter/useCurrentCategory'
import { type RadioButtonBaseProps } from '@shipt/design-system-radio-group/lib/types/index.browser'
import { isKeyboardUser } from '@/features/shared/utils/detectKeyboardUser'
import { Box } from '@shipt/design-system-layouts'
import { useValidSearchParams } from '@/features/shared/hooks/search'
import { type SortOptions } from '@/features/shop/services/Search/types'

export const SortByFilterSection = memo(
  ({
    sortOptions,
    sortDefaultValue,
  }: {
    sortOptions?: SortOptions[]
    sortDefaultValue?: string | null
  }) => {
    const currentCategory = useCurrentCategory()
    const { sortByOptions, defaultOption: hardcodedDefaultOption } = useGetSortByOptions()
    const searchParams = useValidSearchParams()
    const { onChangeFilter } = useOnChangeFilter()
    const trackFilterUtilityClicked = useTrackFilterUtilityClicked()
    const { sort } = searchParams
    const defaultOption = sortDefaultValue ?? hardcodedDefaultOption

    const handleOnChangeFilter = (
      e: ChangeEvent<HTMLInputElement> | KeyboardEvent<HTMLInputElement>,
    ) => {
      const option = e.currentTarget.value
      const isDefaultOption = option === defaultOption
      // we send undefined to reset the sort by option if default option is selected
      onChangeFilter({ sort: isDefaultOption ? undefined : option })

      const filterValueIndex = sortOptions
        ? sortOptions.findIndex((sortOption) => sortOption.value === option)
        : Object.keys(sortByOptions).indexOf(option)

      trackFilterUtilityClicked({
        ...(!isDefaultOption && { params: { sort: option } }),
        filter_active: !isDefaultOption,
        filter_value: option,
        list_index: filterValueIndex,
        utility_type: 'filter',
        lineage: currentCategory?.lineage,
        display_format: DISPLAY_FORMAT.VERTICAL,
        display_sublocation: `${SEARCH_FILTER_TYPE.SORT_BY}_${DISPLAY_FORMAT.VERTICAL}`,
      })
    }

    const handleOnKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
      if (isButtonActionEvent(e)) {
        e.preventDefault()
        handleOnChangeFilter(e)
      }
    }

    const getHardcodedFilterOptions = () => {
      return Object.entries(sortByOptions).map(([option, label]): RadioButtonBaseProps => {
        const defaultValue = sort || defaultOption
        const defaultCheckedOption = option === defaultValue
        return {
          label,
          value: option,
          checked: defaultCheckedOption,
          onChange: (e) => !isKeyboardUser() && handleOnChangeFilter(e),
          onKeyDown: handleOnKeyPress,
        }
      })
    }

    const getDynamicFilterOptions = () => {
      return sortOptions?.map(({ name, value }) => {
        const defaultCheckedOption = value === (sort ?? defaultOption)
        return {
          label: name,
          value,
          checked: defaultCheckedOption,
          onChange: (e: ChangeEvent<HTMLInputElement> | KeyboardEvent<HTMLInputElement>) =>
            !isKeyboardUser() && handleOnChangeFilter(e),
          onKeyDown: handleOnKeyPress,
        }
      })
    }

    const getFilterOptions = () => {
      if (sortOptions?.length) return getDynamicFilterOptions()
      return getHardcodedFilterOptions()
    }

    return (
      <Box paddingTop="sm">
        <RadioGroup
          name={SORT_BY_FACET.name}
          id={SORT_BY_FACET.name}
          options={getFilterOptions() ?? []}
          size="sm"
        />
      </Box>
    )
  },
)
